import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Ticket, Users, Building2, Megaphone, LogOut, Globe2, DollarSign, TrendingUp,
  Calendar, Plane, Hotel, Car, Package, Trash2, Plus, Search, ArrowUpRight, Sparkles, Save,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", icon: Ticket },
  { id: "users", label: "Users", icon: Users },
  { id: "listings", label: "Listings", icon: Building2 },
  { id: "marketing", label: "Marketing & SEO", icon: Megaphone },
];

const TYPE_ICON = { flight: Plane, hotel: Hotel, car: Car, package: Package };
const STATUS_COLORS = { confirmed: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700", cancelled: "bg-red-50 text-red-600", completed: "bg-blue-50 text-blue-700" };
const PIE = ["#0057FF", "#FF4D4D", "#10B981", "#F59E0B"];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login?next=/admin");
  }, [user, loading]);

  if (loading || !user) return null;
  if (user.role !== "admin") return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
      <Building2 className="w-14 h-14 text-slate-300 mb-4" />
      <h1 className="font-heading font-black text-2xl text-slate-950">Admin access required</h1>
      <p className="text-slate-500 mt-2">Your account doesn't have permission to view this area.</p>
      <Button className="mt-6 rounded-full bg-brand" onClick={() => navigate("/")}>Back to site</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 bg-slate-950 text-white flex-col hidden lg:flex fixed inset-y-0">
        <div className="p-6 flex items-center gap-2.5 border-b border-slate-800">
          <div className="w-9 h-9 rounded-lg bg-brand flex items-center justify-center"><Globe2 className="w-5 h-5" /></div>
          <div className="font-heading font-extrabold text-sm leading-tight">WORLD TOUR HUB<div className="text-[10px] text-brand tracking-widest">ADMIN CONSOLE</div></div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${tab === n.id ? "bg-brand text-white" : "text-slate-400 hover:bg-slate-900 hover:text-white"}`}>
              <n.icon className="w-4 h-4" />{n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800 space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-400 hover:bg-slate-900 hover:text-white"><ArrowUpRight className="w-4 h-4" />View site</button>
          <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-400 hover:bg-slate-900"><LogOut className="w-4 h-4" />Sign out</button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="font-heading font-black text-xl text-slate-950 capitalize">{NAV.find((n) => n.id === tab)?.label}</h1>
            <p className="text-xs text-slate-500">Welcome back, {user.name}</p>
          </div>
          <div className="flex lg:hidden gap-1">
            {NAV.map((n) => <button key={n.id} onClick={() => setTab(n.id)} className={`p-2 rounded-lg ${tab === n.id ? "bg-brand text-white" : "text-slate-400"}`}><n.icon className="w-4 h-4" /></button>)}
          </div>
        </header>
        <main className="p-6">
          {tab === "overview" && <Overview />}
          {tab === "bookings" && <Bookings />}
          {tab === "users" && <UsersTab />}
          {tab === "listings" && <Listings />}
          {tab === "marketing" && <Marketing />}
        </main>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent, delay }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    className="bg-white border border-slate-200 rounded-2xl p-6">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${accent}`}><Icon className="w-5 h-5" /></div>
    <div className="font-heading font-black text-3xl text-slate-950">{value}</div>
    <div className="text-sm text-slate-500 font-semibold mt-1">{label}</div>
  </motion.div>
);

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Total revenue" value={money(s.total_revenue)} accent="bg-emerald-50 text-emerald-600" delay={0} />
        <StatCard icon={Ticket} label="Total bookings" value={s.total_bookings} accent="bg-brand/10 text-brand" delay={0.05} />
        <StatCard icon={Users} label="Registered users" value={s.total_users} accent="bg-amber-50 text-amber-600" delay={0.1} />
        <StatCard icon={TrendingUp} label="Pending" value={s.pending} accent="bg-blue-50 text-blue-600" delay={0.15} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-heading font-bold text-slate-950 mb-4">Revenue — last 6 months</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={s.monthly_revenue}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0057FF" stopOpacity={0.3} /><stop offset="100%" stopColor="#0057FF" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#0057FF" strokeWidth={3} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-heading font-bold text-slate-950 mb-4">Bookings by type</h3>
          {s.by_type.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={s.by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {s.by_type.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm">No data yet.</p>}
          <div className="flex flex-wrap gap-3 mt-2">
            {s.by_type.map((t, i) => <span key={t.type} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><span className="w-3 h-3 rounded-full" style={{ background: PIE[i % PIE.length] }} />{t.type} ({t.count})</span>)}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-slate-950 mb-4">Recent bookings</h3>
        <div className="space-y-2">
          {s.recent_bookings.length ? s.recent_bookings.map((b) => {
            const Icon = TYPE_ICON[b.type] || Package;
            return (
              <div key={b.id} className="flex items-center gap-4 py-3 border-b border-slate-50 last:border-0">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="w-4 h-4 text-slate-500" /></div>
                <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-slate-900 truncate">{b.title}</div><div className="text-xs text-slate-400">{b.reference} · {b.user_email}</div></div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${STATUS_COLORS[b.status]}`}>{b.status}</span>
                <div className="font-heading font-bold text-slate-900">{money(b.amount)}</div>
              </div>
            );
          }) : <p className="text-slate-400 text-sm">No bookings yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Bookings() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/bookings", { params: { status: filter } }).then(({ data }) => setItems(data.results));
  useEffect(() => { load(); }, [filter]);

  const update = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Update failed"); }
  };
  const filtered = items.filter((b) => !q || b.reference.toLowerCase().includes(q.toLowerCase()) || b.user_email.toLowerCase().includes(q.toLowerCase()) || b.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-bookings">
      <div className="p-5 flex items-center gap-3 border-b border-slate-100 flex-wrap">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, email, title…" className="pl-9" data-testid="booking-search" /></div>
        <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-44" data-testid="booking-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "confirmed", "pending", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="p-4">Reference</th><th className="p-4">Customer</th><th className="p-4">Trip</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Action</th>
          </tr></thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="p-4 font-mono font-bold text-slate-900">{b.reference}</td>
                <td className="p-4"><div className="font-semibold text-slate-900">{b.user_name || "—"}</div><div className="text-xs text-slate-400">{b.user_email}</div></td>
                <td className="p-4 max-w-[220px] truncate text-slate-700">{b.title}</td>
                <td className="p-4 font-heading font-bold text-slate-900">{money(b.amount)}</td>
                <td className="p-4"><span className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${STATUS_COLORS[b.status]}`}>{b.status}</span></td>
                <td className="p-4">
                  <Select value={b.status} onValueChange={(v) => update(b.id, v)}><SelectTrigger className="w-32 h-8 text-xs" data-testid={`booking-status-${b.reference}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["confirmed", "pending", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="p-12 text-center text-slate-400">No bookings found.</div>}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-users">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
          <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Joined</th>
        </tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="p-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-slate-950 text-white flex items-center justify-center text-xs font-bold">{(u.name || u.email)[0].toUpperCase()}</div><span className="font-semibold text-slate-900">{u.name || "—"}</span></div></td>
              <td className="p-4 text-slate-600">{u.email}</td>
              <td className="p-4"><span className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${u.role === "admin" ? "bg-brand/10 text-brand" : "bg-slate-100 text-slate-600"}`}>{u.role}</span></td>
              <td className="p-4 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {users.length === 0 && <div className="p-12 text-center text-slate-400">No users yet.</div>}
    </div>
  );
}

function Listings() {
  const [hotels, setHotels] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", country: "", star_rating: "4", price_per_night: "", rating: "9.0", image: "" });
  const load = () => { api.get("/admin/hotels").then(({ data }) => setHotels(data.results)); api.get("/packages").then(({ data }) => setPackages(data.results)); };
  useEffect(() => { load(); }, []);

  const addHotel = async () => {
    if (!form.name || !form.city || !form.price_per_night) { toast.error("Name, city and price are required"); return; }
    try {
      await api.post("/admin/hotels", {
        ...form, star_rating: Number(form.star_rating), price_per_night: Number(form.price_per_night), rating: Number(form.rating),
        reviews_count: 0, amenities: ["Free WiFi", "Restaurant"], address: `Central ${form.city}`,
        image: form.image || "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200",
        description: `${form.name} in ${form.city}.`,
        room_types: [{ name: "Standard Room", price: Number(form.price_per_night), beds: "1 Queen", refundable: true }],
      });
      toast.success("Hotel added"); setForm({ name: "", city: "", country: "", star_rating: "4", price_per_night: "", rating: "9.0", image: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delHotel = async (id) => { await api.delete(`/admin/hotels/${id}`); toast.success("Deleted"); load(); };
  const delPkg = async (id) => { await api.delete(`/admin/packages/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6" data-testid="admin-listings">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-slate-950 mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-brand" />Add a new hotel</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>Hotel name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="hotel-name-input" /></div>
          <div><Label>City *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" data-testid="hotel-city-input" /></div>
          <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Price / night ($) *</Label><Input type="number" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} className="mt-1.5" data-testid="hotel-price-input" /></div>
          <div><Label>Star rating</Label><Input type="number" min="1" max="5" value={form.star_rating} onChange={(e) => setForm({ ...form, star_rating: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1.5" placeholder="https://…" /></div>
        </div>
        <Button onClick={addHotel} className="mt-4 rounded-full bg-brand hover:bg-brand-hover" data-testid="add-hotel-btn"><Plus className="w-4 h-4" />Add hotel</Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-heading font-bold text-slate-950 mb-4">Hotels ({hotels.length})</h3>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {hotels.map((h) => (
              <div key={h.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                <img src={h.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-slate-900 truncate">{h.name}</div><div className="text-xs text-slate-400">{h.city} · {money(h.price_per_night)}/night · {h.star_rating}★</div></div>
                <button onClick={() => delHotel(h.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" data-testid={`del-hotel-${h.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-heading font-bold text-slate-950 mb-4">Packages ({packages.length})</h3>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {packages.map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-slate-900 truncate">{p.title}</div><div className="text-xs text-slate-400">{p.destination} · {money(p.price)} · {p.nights}n</div></div>
                <button onClick={() => delPkg(p.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" data-testid={`del-pkg-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Marketing() {
  const [s, setS] = useState({ google_ads_id: "", meta_pixel_id: "", ga4_id: "", seo_title: "", seo_description: "", seo_keywords: "" });
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS({ ...s, ...data })).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/settings", s); toast.success("Marketing settings saved & live"); } catch { toast.error("Save failed"); } };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-marketing">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-slate-950 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-brand" />Ad platform tracking</h3>
        <p className="text-sm text-slate-500 mt-1">Paste your IDs and tracking scripts inject automatically across the live site.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div><Label>Google Ads / Tag ID</Label><Input value={s.google_ads_id} onChange={(e) => setS({ ...s, google_ads_id: e.target.value })} className="mt-1.5" placeholder="AW-XXXXXXXXX" data-testid="google-ads-input" /></div>
          <div><Label>GA4 Measurement ID</Label><Input value={s.ga4_id} onChange={(e) => setS({ ...s, ga4_id: e.target.value })} className="mt-1.5" placeholder="G-XXXXXXXXXX" /></div>
          <div><Label>Meta (Facebook) Pixel ID</Label><Input value={s.meta_pixel_id} onChange={(e) => setS({ ...s, meta_pixel_id: e.target.value })} className="mt-1.5" placeholder="1234567890" data-testid="meta-pixel-input" /></div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-slate-950 flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand" />SEO defaults</h3>
        <div className="space-y-4 mt-5">
          <div><Label>Meta title</Label><Input value={s.seo_title} onChange={(e) => setS({ ...s, seo_title: e.target.value })} className="mt-1.5" placeholder="Book Flights, Hotels & Holidays — World Tour Hub" /></div>
          <div><Label>Meta description</Label><Input value={s.seo_description} onChange={(e) => setS({ ...s, seo_description: e.target.value })} className="mt-1.5" placeholder="Compare and book cheap flights, hotels and packages…" /></div>
          <div><Label>Keywords</Label><Input value={s.seo_keywords} onChange={(e) => setS({ ...s, seo_keywords: e.target.value })} className="mt-1.5" placeholder="cheap flights, hotels, holidays, car hire" /></div>
        </div>
      </div>
      <Button onClick={save} className="rounded-full bg-brand hover:bg-brand-hover h-11 px-8 font-heading font-bold gap-2" data-testid="save-marketing"><Save className="w-4 h-4" />Save & publish</Button>
    </div>
  );
}
